import { useRef, useState, useCallback, useEffect } from 'react';
import { copyText } from '../utils/clipboard';
import './ContentCard.css';

interface ContentCardProps {
  id: string;
  content: string;
  selected?: boolean;
  onSelect?: (id: string) => void;
  onEdit?: (id: string, newContent: string) => void;
  onRegenerate?: (id: string, instructions?: string) => void;
  /** Present only when this field has a pre-regeneration value to restore. */
  onUndo?: () => void;
  regenerating?: boolean;
  regenStats?: { tokens: number; elapsedSec: number } | null;
  showCheckbox?: boolean;
  label?: string;
  compact?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  stale?: boolean;
  emptyPlaceholder?: string;
}

export default function ContentCard({
  id,
  content,
  selected = false,
  onSelect,
  onEdit,
  onRegenerate,
  onUndo,
  regenerating = false,
  regenStats = null,
  showCheckbox = true,
  label,
  compact = false,
  disabled = false,
  readOnly = false,
  stale = false,
  emptyPlaceholder,
}: ContentCardProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const instructionsInputRef = useRef<HTMLInputElement>(null);
  const bodyClickTimerRef = useRef<number | null>(null);
  const [isExpanded, setIsExpanded] = useState(!compact);
  const [isFocused, setIsFocused] = useState(false);
  const [copied, setCopied] = useState(false);
  const [regenPopoverOpen, setRegenPopoverOpen] = useState(false);
  const [regenInstructions, setRegenInstructions] = useState('');

  // Whole-card selection: any card that shows a checkbox toggles on a click
  // anywhere on it. The checkbox stays as the state indicator. Action icons
  // (copy / regenerate / checkbox / collapse) stopPropagation, so they never
  // trigger selection. The editable body needs one extra rule: single click
  // toggles, DOUBLE click enters inline editing (clicks while editing just
  // move the cursor).
  const selectable = showCheckbox && !!onSelect && !disabled;
  const bodyEditable = !disabled && !readOnly && !!onEdit;

  useEffect(() => {
    return () => {
      if (bodyClickTimerRef.current) window.clearTimeout(bodyClickTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(t);
  }, [copied]);

  // Sync contentEditable with content prop
  useEffect(() => {
    if (contentRef.current && !isFocused) {
      contentRef.current.textContent = content || '';
    }
  }, [content, isFocused]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    if (contentRef.current && onEdit) {
      const newText = contentRef.current.textContent ?? '';
      // Guard against an empty blur wiping existing content (2026-07-05 fix):
      // an accidental select-all-delete, then click-away, used to fire
      // onEdit('') and turn a real value into a phantom empty selection. If the
      // field had content and the box is now blank, treat it as no change and
      // restore the DOM text.
      if (newText.trim() === '' && (content ?? '').trim() !== '') {
        contentRef.current.textContent = content || '';
        return;
      }
      if (newText !== content) {
        onEdit(id, newText);
      }
    }
  }, [id, content, onEdit]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleCheckboxClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (onSelect && !disabled) {
      onSelect(id);
    }
  }, [id, onSelect, disabled]);

  const handleCardClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Collapsed compact card: the click's existing primary action is expand.
    if (compact && !isExpanded) {
      setIsExpanded(true);
      return;
    }
    if (!selectable || !onSelect) return;
    // While inline-editing, clicks position the cursor — never toggle.
    if (isFocused) return;

    const inBody = !!contentRef.current && contentRef.current.contains(e.target as Node);
    if (inBody && bodyEditable) {
      // Debounce so a double-click (edit intent) can cancel the toggle. This
      // also avoids the eviction side effect of toggle-then-revert when the
      // selection is already at its required count.
      if (bodyClickTimerRef.current) window.clearTimeout(bodyClickTimerRef.current);
      bodyClickTimerRef.current = window.setTimeout(() => {
        bodyClickTimerRef.current = null;
        onSelect(id);
      }, 250);
    } else {
      onSelect(id);
    }
  }, [compact, isExpanded, selectable, onSelect, isFocused, bodyEditable, id]);

  // Double-click on the body = enter inline editing (single click now selects).
  const handleCardDoubleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!selectable || !bodyEditable || isFocused) return;
    const inBody = !!contentRef.current && contentRef.current.contains(e.target as Node);
    if (!inBody) return;
    if (bodyClickTimerRef.current) {
      window.clearTimeout(bodyClickTimerRef.current);
      bodyClickTimerRef.current = null;
    }
    contentRef.current?.focus();
  }, [selectable, bodyEditable, isFocused]);

  // On selectable cards the editable body must not grab focus on a plain
  // single click (that click is a select). Editing is entered by double click,
  // which focuses programmatically above. Once focused, mousedown behaves
  // normally so the caret and text selection work.
  const handleBodyMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (selectable && bodyEditable && !isFocused) {
      e.preventDefault();
    }
  }, [selectable, bodyEditable, isFocused]);

  const handleCollapseClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsExpanded(false);
  }, []);

  const handleRegenerateClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!onRegenerate || disabled || regenerating) return;
    setRegenPopoverOpen((prev) => !prev);
  }, [onRegenerate, disabled, regenerating]);

  const submitRegen = useCallback(() => {
    if (!onRegenerate) return;
    const trimmed = regenInstructions.trim();
    setRegenPopoverOpen(false);
    setRegenInstructions('');
    onRegenerate(id, trimmed || undefined);
  }, [id, onRegenerate, regenInstructions]);

  const handleInstructionsKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitRegen();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setRegenPopoverOpen(false);
      setRegenInstructions('');
    }
  }, [submitRegen]);

  useEffect(() => {
    if (!regenPopoverOpen) return;
    instructionsInputRef.current?.focus();
    const onMouseDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setRegenPopoverOpen(false);
        setRegenInstructions('');
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [regenPopoverOpen]);

  const handleCopyClick = useCallback(async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const text = contentRef.current?.textContent ?? content ?? '';
    if (!text) return;
    // Clipboard API with execCommand fallback — same util as every other copy
    // control in the app (utils/clipboard.ts). Silent on failure by design.
    const ok = await copyText(text);
    if (ok) setCopied(true);
  }, [content]);

  return (
    <div
      className={`content-card ${selected ? 'card-selected' : ''} ${disabled ? 'card-disabled' : ''} ${readOnly ? 'card-readonly' : ''} ${stale ? 'card-stale' : ''} ${isFocused ? 'card-editing' : ''} ${selectable ? 'card-selectable' : ''}`}
      onClick={handleCardClick}
      onDoubleClick={handleCardDoubleClick}
    >
      {/* Header row: label + checkbox */}
      <div className="card-header">
        {label && <span className="card-label">{label}</span>}
        {readOnly && <span className="card-readonly-badge">Reference</span>}
        {stale && <span className="card-stale-badge">Stale</span>}
        <div className="card-header-spacer" />
        {regenStats && (
          <span className="card-regen-stats" title="Last regeneration stats">
            {regenStats.tokens >= 1000 ? `${(regenStats.tokens / 1000).toFixed(1)}k` : regenStats.tokens} · {regenStats.elapsedSec}s
          </span>
        )}
        {copied && <span className="card-copy-flash" aria-live="polite">Copied!</span>}
        <button
          className={`card-copy-btn ${copied ? 'copied' : ''}`}
          onClick={handleCopyClick}
          title="Copy to clipboard"
          aria-label="Copy to clipboard"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <rect x="4" y="3" width="7" height="9" rx="1.2" stroke="currentColor" strokeWidth="1.5" fill="none" />
            <path d="M3 10V2.5a1 1 0 0 1 1-1h5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
          </svg>
        </button>
        {onUndo && !readOnly && (
          <button
            className="card-undo-btn"
            onClick={(e) => { e.stopPropagation(); onUndo(); }}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={disabled || regenerating}
            title="Go back — restore the value this field had before it was regenerated"
            aria-label="Restore previous value"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M5.5 2.5L2.5 5.5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              <path d="M2.5 5.5h6a3 3 0 0 1 0 6H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
            </svg>
          </button>
        )}
        {onRegenerate && !readOnly && (
          <button
            className={`card-regen-btn ${regenerating ? 'regenerating' : ''}`}
            onClick={handleRegenerateClick}
            disabled={disabled || regenerating}
            title="Regenerate this field"
            aria-label="Regenerate this field"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path
                d="M11.5 3.5a5 5 0 1 0 1.2 5.2"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                fill="none"
              />
              <path
                d="M12.5 1.5v3h-3"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </button>
        )}
        {showCheckbox && (
          <button
            className={`card-checkbox ${selected ? 'checked' : ''}`}
            onClick={handleCheckboxClick}
            disabled={disabled}
            aria-label={selected ? 'Deselect' : 'Select'}
          >
            {selected && (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
        )}
      </div>

      {regenPopoverOpen && (
        <div ref={popoverRef} className="card-regen-popover" onClick={(e) => e.stopPropagation()}>
          <input
            ref={instructionsInputRef}
            type="text"
            className="card-regen-instructions"
            placeholder="Instructions (optional)"
            value={regenInstructions}
            onChange={(e) => setRegenInstructions(e.target.value)}
            onKeyDown={handleInstructionsKeyDown}
          />
          <button className="card-regen-go" onClick={submitRegen} title="Regenerate">
            Go
          </button>
        </div>
      )}

      {/* Card body — contentEditable for inline editing */}
      <div
        ref={contentRef}
        className={`card-body content-text ${compact && !isExpanded ? 'card-compact' : ''}`}
        contentEditable={bodyEditable}
        suppressContentEditableWarning
        onBlur={handleBlur}
        onFocus={handleFocus}
        onMouseDown={handleBodyMouseDown}
        spellCheck={false}
        data-placeholder={emptyPlaceholder || undefined}
        title={selectable && bodyEditable && !isFocused ? 'Click to select · double-click to edit' : undefined}
      />

      {/* Compact mode collapse button */}
      {compact && isExpanded && (
        <button className="card-collapse-btn" onClick={handleCollapseClick}>
          Collapse
        </button>
      )}
    </div>
  );
}
